import { useState, useEffect } from 'react'
import { k8sResourceService } from '@/services/k8sResourceService'
import SearchableSelector from './SearchableSelector'
import SearchableInput from './SearchableInput'

interface AnnotationItem {
  id: string
  key: string
  value: string
}

interface TLSConfig {
  enabled: boolean
  secretName: string
}

interface IngressConfigData {
  enabled: boolean
  host: string
  tls: TLSConfig
  className: string
  annotations: Record<string, string>
}

interface IngressConfigProps {
  value: IngressConfigData
  onChange: (config: IngressConfigData) => void
  disabled?: boolean
  namespace?: string
  className?: string
}

export default function IngressConfig({
  value,
  onChange,
  disabled = false,
  namespace,
  className = ''
}: IngressConfigProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [tlsSecrets, setTlsSecrets] = useState<string[]>([])
  const [secretsLoading, setSecretsLoading] = useState(false)
  const [ingressClasses, setIngressClasses] = useState<string[]>([])
  const [ingressClassesLoading, setIngressClassesLoading] = useState(false)
  const [annotationItems, setAnnotationItems] = useState<AnnotationItem[]>([])

  // 將 Record<string, string> 轉換為 AnnotationItem[]
  const convertToAnnotationItems = (annotations: Record<string, string>): AnnotationItem[] => {
    return Object.entries(annotations).map(([key, value], index) => ({
      id: `annotation-${Date.now()}-${index}`,
      key,
      value
    }))
  }

  // 將 AnnotationItem[] 轉換為 Record<string, string>
  const convertToAnnotationsRecord = (items: AnnotationItem[]): Record<string, string> => {
    const record: Record<string, string> = {}
    items.forEach(item => {
      if (item.key.trim()) {
        record[item.key] = item.value
      }
    })
    return record
  }

  // 獲取 Ingress Classes
  useEffect(() => {
    const fetchIngressClasses = async () => {
      if (!isExpanded) return

      setIngressClassesLoading(true)
      try {
        const classes = await k8sResourceService.getIngressClasses()
        const classNames = k8sResourceService.extractIngressClassNames(classes)
        setIngressClasses(classNames)
      } catch (error) {
        console.error('Failed to fetch ingress classes:', error)
        setIngressClasses(['nginx', 'traefik', 'haproxy']) // Fallback
      } finally {
        setIngressClassesLoading(false)
      }
    }

    fetchIngressClasses()
  }, [isExpanded])

  // 當 namespace 改變或展開時獲取 TLS secrets
  useEffect(() => {
    const fetchTLSSecrets = async () => {
      if (!isExpanded || !namespace) return

      setSecretsLoading(true)
      try {
        const secrets = await k8sResourceService.getSecrets(namespace)
        const tlsSecretNames = k8sResourceService.extractTLSSecretNames(secrets)
        setTlsSecrets(tlsSecretNames)
      } catch (error) {
        console.error('Failed to fetch TLS secrets:', error)
        setTlsSecrets([])
      } finally {
        setSecretsLoading(false)
      }
    }

    fetchTLSSecrets()
  }, [isExpanded, namespace])

  // 初始化 annotationItems
  useEffect(() => {
    const items = convertToAnnotationItems(value.annotations)
    setAnnotationItems(items)
  }, []) // 只在組件首次加載時運行

  const handleInputChange = (field: keyof IngressConfigData, newValue: any) => {
    onChange({
      ...value,
      [field]: newValue
    })
  }

  const handleTLSChange = (field: keyof TLSConfig, newValue: any) => {
    onChange({
      ...value,
      tls: {
        ...value.tls,
        [field]: newValue
      }
    })
  }

  const updateAnnotations = (updatedItems: AnnotationItem[]) => {
    setAnnotationItems(updatedItems)
    const newAnnotations = convertToAnnotationsRecord(updatedItems)
    handleInputChange('annotations', newAnnotations)
  }

  const updateAnnotationKey = (id: string, newKey: string) => {
    const updatedItems = annotationItems.map(item =>
      item.id === id ? { ...item, key: newKey } : item
    )
    updateAnnotations(updatedItems)
  }

  const updateAnnotationValue = (id: string, newValue: string) => {
    const updatedItems = annotationItems.map(item =>
      item.id === id ? { ...item, value: newValue } : item
    )
    updateAnnotations(updatedItems)
  }

  const addAnnotation = () => {
    const newItem: AnnotationItem = {
      id: `annotation-${Date.now()}-${Math.random()}`,
      key: '',
      value: ''
    }
    const updatedItems = [...annotationItems, newItem]
    updateAnnotations(updatedItems)
  }

  const removeAnnotation = (id: string) => {
    const updatedItems = annotationItems.filter(item => item.id !== id)
    updateAnnotations(updatedItems)
  }

  const ChevronDownIcon = () => (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
    </svg>
  )

  const GlobeIcon = () => (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9m0 9a9 9 0 009-9m-9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
    </svg>
  )

  const PlusIcon = () => (
    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  )

  const TrashIcon = () => (
    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  )

  return (
    <div className={`border border-secondary-300 rounded-lg bg-background ${className}`}>
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-secondary-50 transition-colors duration-200"
        onClick={() => !disabled && setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-secondary-100 rounded-full flex items-center justify-center">
              <GlobeIcon />
            </div>
          </div>
          <div>
            <h3 className="text-sm font-medium text-text-dark">
              Ingress Configuration
            </h3>
            <p className="text-xs text-text-dark/60">
              {value.enabled ? `Ingress enabled${value.host ? ` - ${value.host}` : ''}` : 'Ingress disabled'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Enable/Disable Toggle */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              if (!disabled) {
                handleInputChange('enabled', !value.enabled)
              }
            }}
            disabled={disabled}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-200 ${
              value.enabled ? 'bg-primary' : 'bg-secondary-300'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span
              className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform duration-200 ${
                value.enabled ? 'translate-x-5' : 'translate-x-1'
              }`}
            />
          </button>

          {/* Expand/Collapse Arrow */}
          <div
            className={`text-text-dark/40 transition-transform duration-200 ${
              isExpanded ? 'rotate-180' : ''
            }`}
          >
            <ChevronDownIcon />
          </div>
        </div>
      </div>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-secondary-200 bg-secondary-50/30">
          <div className="pt-4 space-y-4">
            {/* Hostname */}
            <div>
              <label className="block text-xs font-medium text-text-dark mb-2">
                Hostname <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={value.host}
                onChange={(e) => handleInputChange('host', e.target.value)}
                disabled={disabled || !value.enabled}
                placeholder="example.com"
                className="w-full px-3 py-2 text-sm border border-secondary-300 rounded-lg bg-background text-text-dark placeholder-text-dark/50 focus:ring-2 focus:ring-primary-200 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              />
            </div>

            {/* Ingress Class */}
            <div>
              <SearchableSelector
                title="Ingress Class"
                value={value.className}
                onChange={(className) => handleInputChange('className', className)}
                options={ingressClasses}
                placeholder="Select or type ingress class..."
                loading={ingressClassesLoading}
                disabled={disabled || !value.enabled}
                emptyMessage="No ingress classes available"
                allowClear={false}
                showCount={false}
                className="w-full"
              />
            </div>

            {/* TLS Configuration */}
            <div className="bg-background rounded-lg border border-secondary-200 p-3">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <label className="text-xs font-medium text-text-dark">
                    TLS Configuration
                  </label>
                  <p className="text-xs text-text-dark/60 mt-1">
                    Enable HTTPS with SSL certificate
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleTLSChange('enabled', !value.tls.enabled)}
                  disabled={disabled || !value.enabled}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-200 ${
                    value.tls.enabled ? 'bg-primary' : 'bg-secondary-300'
                  } ${disabled || !value.enabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span
                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform duration-200 ${
                      value.tls.enabled ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {value.tls.enabled && (
                <div>
                  <SearchableInput
                    title="TLS Secret Name"
                    value={value.tls.secretName}
                    onChange={(secretName) => handleTLSChange('secretName', secretName)}
                    options={tlsSecrets}
                    placeholder="Type or select TLS secret..."
                    loading={secretsLoading}
                    disabled={disabled || !value.enabled}
                    emptyMessage={`No TLS secrets in ${namespace || 'namespace'}`}
                    allowCustom={true}
                    showCount={false}
                    className="w-full"
                  />
                  <p className="mt-1 text-xs text-text-dark/60">
                    You can select existing TLS secrets or type a custom name (e.g., for cert-manager auto-generation)
                  </p>
                </div>
              )}
            </div>

            {/* Custom Annotations */}
            <div className="bg-background rounded-lg border border-secondary-200 p-3">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <label className="text-xs font-medium text-text-dark">
                    Annotations
                  </label>
                  <p className="text-xs text-text-dark/60 mt-1">
                    Add custom annotations for ingress configuration
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addAnnotation}
                  disabled={disabled || !value.enabled}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <PlusIcon />
                  Add Annotation
                </button>
              </div>

              {annotationItems.length > 0 ? (
                <div className="space-y-2">
                  {annotationItems.map((item) => (
                    <div key={item.id} className="bg-secondary-50/50 rounded-lg p-3 border border-secondary-200/50">
                      <div className="flex gap-3 items-center">
                        <div className="flex-1">
                          <input
                            type="text"
                            value={item.key}
                            onChange={(e) => updateAnnotationKey(item.id, e.target.value)}
                            disabled={disabled || !value.enabled}
                            placeholder="nginx.ingress.kubernetes.io/ssl-redirect"
                            className="w-full px-3 py-2 text-sm border border-secondary-300 rounded-lg bg-background text-text-dark placeholder-text-dark/50 focus:ring-2 focus:ring-primary-200 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                          />
                        </div>
                        <div className="flex-1">
                          <input
                            type="text"
                            value={item.value}
                            onChange={(e) => updateAnnotationValue(item.id, e.target.value)}
                            disabled={disabled || !value.enabled}
                            placeholder="true"
                            className="w-full px-3 py-2 text-sm border border-secondary-300 rounded-lg bg-background text-text-dark placeholder-text-dark/50 focus:ring-2 focus:ring-primary-200 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAnnotation(item.id)}
                          disabled={disabled || !value.enabled}
                          className="flex items-center justify-center w-9 h-9 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                          title="Remove annotation"
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-6 border border-dashed border-secondary-300 rounded-lg bg-secondary-50/30">
                  <div className="w-8 h-8 text-text-dark/30 mb-2">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
                    </svg>
                  </div>
                  <p className="text-xs text-text-dark/60 text-center font-medium">No annotations added</p>
                  <p className="text-xs text-text-dark/40 text-center mt-1">Click "Add Annotation" to get started</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}